import { useUserStore, usePageStore } from '@/store';
import { mapState as rootMapState } from 'pinia';
import util from '@/common/util';
import { Preview, PreviewNum } from '@/common/utils';
import HouseFormat, {
    mapHouseData,
    getBasic,
    getProviderText
} from '@/common/house';
import NoDisclosure from '@/components/nodisclosure.vue';
import detailPop from '@/components/global/detail-pop/index.js';
import TitleMaskV2 from './_component/title-mask-v2.js';
import HouseMask from './_component/house-mask.vue';
import HouseTrend from './_component/house-trend.vue';
import { getHouseSchemaInfo } from '@/common/schemaInfo.js';
import { calcHouseLink } from './useHouse';
//which mlsOrgId to show "*" before sold date
const showStarMLS = [588]
export default {
    i18next: { ns: 'md-house' },
    inject: {
        moreInfo: {
            type: Function,
            default: null
        }
    },
    props: {
        house: {
            type: Object
        }
    },
    data: function() {
        return {
            defHouseImg:
                'https://img.chime.me/imageemb/mls-listing/76/802932547704/7359eed2/1710792987/original_LMqxDsIgEIDhJ5K7Qovo6qSJY9PxUoXQU08vhcHHN6jj9-dfatU9wGXlvNSNpMgzdkbXaH5JHsVcX_IfgGXOqQAiIgS0O3B22ELwLnzZ1EAW-0Dd4DxNZxoP97eOp6efOB5L6s1N8ycAAP__.jpg',
            previewIndex: 1,
            vowAddress: ''

            // provided: ''
        };
    },
    computed: {
        ...rootMapState(usePageStore, {
            currencySymbol: store => store.currency.symbol,
            currencyAbbr: store => store.currency.abbr,
            listingSearchSwitch: store => store.page.listingSearchSwitch,
            page: 'processedGlobalData',
            forCMS: 'forCMS'
        }),
        ...rootMapState(useUserStore, {
            loginStatus: store => store.loginStatus,
            user: store => store.user,
            userEmailVerified: 'userEmailVerified'
        }),
        houseData({ house, page }) {
            return HouseFormat.call(this, house, page);
        },
        ...mapHouseData('houseData'),
        housePrice({ currencySymbol }) {
            const price = this.soldDate ? this.soldPrice : this.price;
            return this.isShowMask
                ? currencySymbol + '****'
                : price.replace(/\s/g, '');
        },
        provided({ house, listingSearchSwitch }) {
            return listingSearchSwitch
                ? house.mlsOrg?.cardTemplate
                : getProviderText(house.mlsOrg?.cardTemplate, house);
        },
        basic() {
            let bsc = getBasic.call(this, this.house);
            if (this.isShowMask) {
                bsc = bsc.map(item => {
                    return {
                        key: item.key,
                        text: item.text.replace(/[\d|.|,]{1,}/g, '***')
                    };
                });
            }
            return bsc;
        },
        seoSchema({ page }) {
            return getHouseSchemaInfo(this.house, {
                url: this.detailUrl,
                country: page.country,
                location: this.pageLocation,
                currencyAbbr: this.currencyAbbr,
                agentCreator: page.agentCreator
            });
        },
        isMobile() {
            return this.platform.isMobile;
        },
        wordPressSSR({ page }) {
            return page.wordPressSSR;
        },
        widget({ page }) {
            return page.widget;
        },
        previews() {
            return this.previewPictures?.length > 0
                ? this.previewPictures
                : [this.preview];
        },
        showAllPrice({ showListingPrice, showSoldPrice }) {
            return showListingPrice && showSoldPrice;
        },
        hidePrice({ showListingPrice, showSoldPrice, soldDate }) {
            return !showListingPrice && !showSoldPrice && soldDate;
        },
        inPopDetailPage() {
            return util.inPopDetailPage;
        },
        linkData() {
            let link = calcHouseLink(this.houseData)
            const linkAttrs = {
                href: this.transformLink2WordPress(link),
                target: this.detailUrlTarget,
                'aria-label': this.address,
                self: this.inPopDetailPage
            };
            
            // Build rel attribute by combining multiple values
            const relValues = [];
            
            // Add nofollow if noIndex is true
            if (this.noIndex) {
                relValues.push('nofollow');
            }
            
            // Only add rel attribute if there are values
            if (relValues.length > 0) {
                linkAttrs.rel = relValues.join(' ');
            }
            
            return linkAttrs;
        },
        mlsAlt() {
            return (this.house.mlsOrg && this.house.mlsOrg.name) || this.mlsid;
        },
        isShowMask({ userEmailVerified }) {
            return this.isProtected && !userEmailVerified;
        },
        moreListing() {
            if (this.moreInfo) {
                return this.moreInfo();
            }
        },
        firstImage({ previews, defHouseImg }) {
            return previews?.[0] || defHouseImg;
        },
        secondImage({ previews, defHouseImg }) {
            return previews?.[1] || defHouseImg;
        },
        trendStatus({ trend, trendUp }) {
            return trend
                ? trendUp
                    ? 'trend-up'
                    : 'trend-down'
                : '';
        },
        //whether to show "*" before sold date
        showStar({ house }) {
            return showStarMLS.includes(house.mlsOrgId)
        }
    },
    watch: {
        loginStatus(val) {
            if (val === -1) {
                this.maskData();
            } else if (val === 1 && this.isShowMask) {
                this.vowAddress = this.$t('addressNotDisclosed');
            }
        },
        vow() {
            this.maskData();
        },
        ['house.' + Preview.origin](preview) {
            this[Preview.name] = preview;
        },
        ['house.' + PreviewNum.origin](preview) {
            this[PreviewNum.name] = preview;
        }
    },
    created() {
        this.maskData();
    },
    methods: {
        transformLink2WordPress(link) {
            return util.transformLink2WordPress(
                link,
                this.wordPressSSR
            );
        },
        openDetail(houseLink, e) {
            let link = calcHouseLink(this.houseData)
            const curLink = this.transformLink2WordPress(link);
            if (this.isMobile) {
                e.preventDefault();
                e.stopPropagation();
                if (this.inPopDetailPage) {
                    window.parent.__curDetailPop &&
                        window.parent.__curDetailPop.setLink(curLink);
                } else {
                    let openAnimation = this.isInMap
                        ? 'bottomToUp'
                        : 'fadeInRight';
                    //  mobile terminal widget Fail detailPop jump
                    this.detailPopIns = this.widget
                        ? window.open(this.getWidgetUrl(curLink))
                        : detailPop.open(curLink, openAnimation);
                }
            }
        },
        getWidgetUrl(url) {
            if (/global_domain/.test(this.pageLocation.search)) {
                url =
                    url.indexOf('?') > -1
                        ? `${url}&global_domain=${this.page.domainName}`
                        : `${url}?global_domain=${this.page.domainName}`;
                return `https://${window.location.host}${url}`;
            } else {
                return url;
            }
        },
        maskData() {
            if (this.isShowMask) {
                this.vowAddress = this.$t('addressNotDisclosed');
            }
        }
    },
    components: {
        NoDisclosure,
        TitleMaskV2,
        HouseMask,
        HouseTrend
    }
};
